/**
 * RedirectsPanel - manage old address -> new page redirects (owner only).
 * Each rule is 301 (permanent) or 302 (temporary). One optional catch-all ('*') covers every old
 * address that has no rule of its own. Changes save immediately (no Save button needed).
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Trash2, AlertTriangle, ArrowRight, Copy } from 'lucide-react';
import { API } from '@/App';

const INPUT = 'px-3 py-2 bg-zinc-100 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-800 rounded-lg text-zinc-950 dark:text-white text-sm';
const BTN = 'px-3 py-2 rounded-lg text-sm bg-zinc-950 text-white dark:bg-white dark:text-zinc-950 disabled:opacity-50';
const STATUS_NAMES = { 301: '301 Permanent', 302: '302 Temporary', 307: '307 Temporary', 308: '308 Permanent' };

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || String(x)).join('; ');
  return d || e?.message || 'Something went wrong';
};

const targetOf = (r) => (r.to_url ? r.to_url : '/' + (r.to_slug || ''));

const StatusSelect = ({ value, onChange, testId, disabled }) => {
  const opts = [301, 302];
  if (!opts.includes(value)) opts.push(value);
  return (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))} disabled={disabled} data-testid={testId} className={INPUT + ' w-44'}>
      {opts.map((o) => <option key={o} value={o}>{STATUS_NAMES[o] || o}</option>)}
    </select>
  );
};

const parseBulk = (text) => {
  const t = text.trim();
  if (!t) return { mapping: {}, items: [] };
  if (t.startsWith('{')) return { mapping: JSON.parse(t), items: [] };
  const items = [];
  t.split('\n').forEach((line) => {
    const l = line.trim();
    if (!l || l.startsWith('#')) return;
    const parts = l.split(/\s*(?:->|=>|,|\t)\s*/);
    items.push({ source: parts[0] || '', target: parts[1] || '' });
  });
  return { mapping: {}, items };
};

export const RedirectsPanel = ({ projectId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [src, setSrc] = useState('');
  const [tgt, setTgt] = useState('');
  const [status, setStatus] = useState(301);
  const [bulk, setBulk] = useState('');
  const [bulkStatus, setBulkStatus] = useState(301);
  const [bulkRes, setBulkRes] = useState(null);
  const [edge, setEdge] = useState(null);
  const [fbTarget, setFbTarget] = useState('/');
  const [fbStatus, setFbStatus] = useState(301);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/redirects`);
      setRows(res.data.redirects || []);
      const fb = (res.data.redirects || []).find((r) => r.from_slug === '*');
      if (fb) { setFbTarget(targetOf(fb)); setFbStatus(fb.status); }
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const save = async (source, target, st, okText) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/redirects`, { source, target, status: st });
      setMsg({ ok: true, text: okText + (r.data.warning ? ' - note: ' + r.data.warning : '') });
      await load();
      return true;
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const remove = async (source, okText) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/projects/${projectId}/redirects`, { params: { source } });
      setMsg({ ok: true, text: okText });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    if (await save(src, tgt, status, 'Redirect saved')) { setSrc(''); setTgt(''); }
  };

  const runBulk = async () => {
    let parsed;
    try { parsed = parseBulk(bulk); } catch (e) { setMsg({ ok: false, text: 'That is not valid JSON: ' + e.message }); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/redirects/bulk`, { ...parsed, status: bulkStatus });
      setBulkRes(res.data);
      setMsg({ ok: true, text: `Imported: ${res.data.created} new, ${res.data.updated} updated, ${res.data.errors.length} rejected` });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  const loadEdge = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/redirects/edge-config`);
      setEdge(res.data);
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    }
  };

  if (loading) return <Loader2 className='w-4 h-4 animate-spin text-zinc-500' />;
  const catchAll = rows.find((r) => r.from_slug === '*');
  const list = rows.filter((r) => r.from_slug !== '*');

  return (
    <div className='space-y-6' data-testid='redirects-editor'>
      <p className='text-xs text-zinc-500'>
        Send visitors from an old address to a new page. Use 301 (permanent) for pages that moved for good, 302 (temporary) if you plan to change it later.
        Changes save as soon as you make them.
      </p>
      {msg && <p className={`text-xs ${msg.ok ? 'text-emerald-600' : 'text-rose-500'}`} data-testid='redirect-msg'>{msg.text}</p>}

      <div className='rounded-lg border border-zinc-200 dark:border-zinc-800 p-3 space-y-2' data-testid='redirect-catchall'>
        <div className='text-sm font-medium text-zinc-950 dark:text-white'>Everything else (catch-all)</div>
        <p className='text-xs text-zinc-500'>Any old address that has no redirect of its own goes here instead of showing Page not found. Pages that exist are never affected.</p>
        <div className='flex flex-wrap gap-2 items-center'>
          <input value={fbTarget} onChange={(e) => setFbTarget(e.target.value)} placeholder='/ for the home page, or /page, or https://...' className={INPUT + ' flex-1 min-w-[12rem]'} data-testid='redirect-catchall-target' />
          <StatusSelect value={fbStatus} onChange={setFbStatus} testId='redirect-catchall-status' disabled={busy} />
          <button type='button' className={BTN} disabled={busy} onClick={() => save('*', fbTarget, fbStatus, 'Catch-all saved')} data-testid='redirect-catchall-save'>{catchAll ? 'Update' : 'Turn on'}</button>
          {catchAll && <button type='button' className='px-3 py-2 rounded-lg text-sm border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300' disabled={busy} onClick={() => remove('*', 'Catch-all turned off')} data-testid='redirect-catchall-remove'>Turn off</button>}
        </div>
        {catchAll && !catchAll.target_published && <p className='text-xs text-amber-600 flex items-center gap-1'><AlertTriangle className='w-3 h-3' /> The catch-all target is not a published page.</p>}
      </div>

      <div className='space-y-2'>
        <div className='text-sm font-medium text-zinc-950 dark:text-white'>Add a redirect</div>
        <div className='flex flex-wrap gap-2 items-center'>
          <input value={src} onChange={(e) => setSrc(e.target.value)} placeholder='Old address, e.g. /stripe-integration' className={INPUT + ' flex-1 min-w-[10rem]'} data-testid='redirect-add-source' />
          <ArrowRight className='w-4 h-4 text-zinc-500' />
          <input value={tgt} onChange={(e) => setTgt(e.target.value)} placeholder='New page, e.g. /stripe' className={INPUT + ' flex-1 min-w-[10rem]'} data-testid='redirect-add-target' />
          <StatusSelect value={status} onChange={setStatus} testId='redirect-add-status' disabled={busy} />
          <button type='button' className={BTN} disabled={busy || !src.trim() || !tgt.trim()} onClick={add} data-testid='redirect-add-btn'>Add</button>
        </div>
      </div>

      <div className='space-y-2'>
        <div className='text-sm font-medium text-zinc-950 dark:text-white'>Redirects ({list.length})</div>
        {list.length === 0 && <p className='text-xs text-zinc-500' data-testid='redirect-empty'>No redirects yet.</p>}
        <div className='divide-y divide-zinc-200 dark:divide-zinc-800 border border-zinc-200 dark:border-zinc-800 rounded-lg'>
          {list.map((r) => (
            <div key={r.from_slug} className='flex flex-wrap items-center gap-2 px-3 py-2' data-testid={`redirect-row-${r.from_slug}`}>
              <code className='text-xs text-zinc-950 dark:text-white'>/{r.from_slug}</code>
              <ArrowRight className='w-3 h-3 text-zinc-500' />
              <code className='text-xs text-zinc-600 dark:text-zinc-400 flex-1 break-all'>{targetOf(r)}</code>
              {!r.target_published && <span title='The target is not a published page' className='text-amber-600' data-testid={`redirect-warn-${r.from_slug}`}><AlertTriangle className='w-4 h-4' /></span>}
              {r.shadowed && <span title='A published page uses this address, so the redirect is ignored' className='text-amber-600 text-xs'>page exists</span>}
              <StatusSelect value={r.status} onChange={(v) => save(r.from_slug, targetOf(r), v, 'Changed to ' + (STATUS_NAMES[v] || v))} testId={`redirect-status-${r.from_slug}`} disabled={busy} />
              <button type='button' title='Delete' disabled={busy} onClick={() => remove(r.from_slug, 'Redirect deleted')} className='p-2 text-zinc-500 hover:text-rose-500' data-testid={`redirect-delete-${r.from_slug}`}><Trash2 className='w-4 h-4' /></button>
            </div>
          ))}
        </div>
      </div>

      <div className='space-y-2'>
        <div className='text-sm font-medium text-zinc-950 dark:text-white'>Import many at once</div>
        <p className='text-xs text-zinc-500'>One per line: old address, then new page, separated by a comma, a tab or an arrow (-&gt;). You can also paste a JSON map.</p>
        <textarea value={bulk} onChange={(e) => setBulk(e.target.value)} rows={5} placeholder={'/stripe-integration -> /stripe\n/welcome, /what-is-emergent'} className={INPUT + ' w-full font-mono text-xs'} data-testid='redirect-bulk-text' />
        <div className='flex gap-2 items-center'>
          <StatusSelect value={bulkStatus} onChange={setBulkStatus} testId='redirect-bulk-status' disabled={busy} />
          <button type='button' className={BTN} disabled={busy || !bulk.trim()} onClick={runBulk} data-testid='redirect-bulk-btn'>Import</button>
        </div>
        {bulkRes && (
          <div className='text-xs space-y-1' data-testid='redirect-bulk-result'>
            {bulkRes.errors.map((e, i) => <p key={'e' + i} className='text-rose-500'>Rejected {e.source}: {e.error}</p>)}
            {bulkRes.warnings.map((w, i) => <p key={'w' + i} className='text-amber-600'>{w.source}: {w.warning}</p>)}
          </div>
        )}
      </div>

      <div className='space-y-2'>
        <div className='text-sm font-medium text-zinc-950 dark:text-white'>Production redirect rules</div>
        <p className='text-xs text-zinc-500'>A deployed site only sends a real 301/302 when these rules are applied at the platform edge. This builds the rules from your list. Links to other sites and the catch-all are not part of it.</p>
        <button type='button' className={BTN} onClick={loadEdge} data-testid='redirect-edge-btn'>Show rules</button>
        {edge && (
          <div className='space-y-2' data-testid='redirect-edge-output'>
            <textarea readOnly rows={8} value={JSON.stringify({ version: edge.version, redirects: edge.redirects }, null, 2)} className={INPUT + ' w-full font-mono text-xs'} />
            <button type='button' className='flex items-center gap-1 text-xs text-zinc-600 dark:text-zinc-400' onClick={() => navigator.clipboard?.writeText(JSON.stringify({ version: edge.version, redirects: edge.redirects }, null, 2))}><Copy className='w-3 h-3' /> Copy</button>
            {edge.skipped.length > 0 && <p className='text-xs text-zinc-500'>Not included ({edge.skipped.length}): {edge.skipped.map((s) => s.from).join(', ')}</p>}
            <p className='text-xs text-amber-600'>{edge.note}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RedirectsPanel;
